import { captureException } from '@sentry/react';
import { createNanoEvents, Emitter, Unsubscribe } from 'nanoevents';
import withQuery from 'with-query';

import { generalErrorHandler } from '../actions/utils';
import { UserResponse } from '../py-generated/datalouder/third_party_login/types';
import { websocketUrl } from '../services/config';
import { requestConnection } from '../services/websocket.service';

const makeWebSocketURL = (userId: number, token: string): string =>
  withQuery(websocketUrl, { userId, token });

const HEART_BEAT_INTERVAL = 60_000;

/**
 * websocket connection that auto connects using heartbeats
 * It is supposed to be used as a global variable.
 */
class WebSocketConnection {
  private ws: WebSocket;

  private userId: number;
  private browserAuthToken: string;

  private emitter: Emitter;

  private lastPongTime: number;

  constructor() {
    this.ws = null;
    this.userId = null;
    this.browserAuthToken = null;

    this.emitter = createNanoEvents();

    setInterval(() => this.heartbeat(), HEART_BEAT_INTERVAL);
  }

  updateCredential(user: UserResponse, authToken: string): void {
    if (user?.id !== this.userId || authToken !== this.browserAuthToken) {
      this.userId = user?.id;
      this.browserAuthToken = authToken;
      if (this.canMakeConnection()) {
        console.log('User updated, refreshing ws connection...');
        this.cleanUpConnection();
        this.makeConnection();
      }
    }
  }

  isReady(): boolean {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Bind the message event, and return the unbind function.
   * @param callback message callback
   * @returns unbind function
   */
  bindMessage(
    callback: (message: Record<string, unknown>) => void
  ): Unsubscribe {
    return this.emitter.on('message', callback);
  }

  private canMakeConnection() {
    return this.userId && this.browserAuthToken;
  }

  private cleanUpConnection() {
    if (this.ws) {
      console.log('Closing the old ws connection...');
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * generalErrorHandler requires a dispatch which doesn't exist
   * in this scope. Using a dummy dispatch.
   * TODO: allow this class to have a valid dispatch.
   */
  private dummyDispatch = (_: unknown) => _;

  private makeConnection() {
    requestConnection()
      .then(data => {
        const url = makeWebSocketURL(this.userId, data.token);
        this.ws = new WebSocket(url);
        this.setupWebSocketListeners();
      })
      .catch(
        generalErrorHandler(this.dummyDispatch, error => {
          captureException(error);
          console.error('Cannot get ws token:');
          console.error(error);
        })
      );
  }

  private setupWebSocketListeners() {
    this.ws.addEventListener('open', () => this.onConnect());
    this.ws.addEventListener('error', event => this.onError(event));
    this.ws.addEventListener('close', () => this.onDisconnect());
    this.ws.addEventListener('message', event => this.onMessage(event));
  }

  private onConnect() {
    this.lastPongTime = Date.now();
    console.log('ws connected');
  }

  private onDisconnect() {
    this.ws = null;
    console.log('ws disconnected');
  }

  private onError(event: WebSocketEventMap['error']) {
    console.error('Error in ws connection:');
    console.error(event);
  }

  private onMessage(event: MessageEvent) {
    this.lastPongTime = Date.now();
    // for heartbeat messages, only update the lastPongTime
    if (event.data === 'PONG') {
      return;
    }
    // for other messages, send it to the listeners
    try {
      const messageBody = JSON.parse(event.data);
      this.emitter.emit('message', messageBody);
    } catch (error) {
      captureException(error);
    }
  }

  private heartbeat() {
    console.log('ws heartbeat check');
    if (!this.isReady && this.canMakeConnection()) {
      console.log('ws connection is not ready, refreshing ws connection...');
      this.cleanUpConnection();
      this.makeConnection();
    } else if (
      this.isReady() &&
      Date.now() - this.lastPongTime > HEART_BEAT_INTERVAL
    ) {
      // make a ping
      console.log('PING');
      this.ws.send('PING');
      if (Date.now() - this.lastPongTime > 3 * HEART_BEAT_INTERVAL) {
        console.log(
          'ws connection not receiving heartbeats, refreshing ws connection...'
        );
        this.cleanUpConnection();
        this.makeConnection();
      }
    }
  }
}

export default WebSocketConnection;
